import { useEffect, useMemo, useRef, useState } from 'react';
import type { StopStatusFilter } from './SystemMapControls';
import { ALL_STATUSES } from './SystemMapControls';
import {
  clientPortalApi,
  type SystemMapRouteGeometry,
  type SystemMapStopDetail,
  type SystemMapStopsResponse,
  type UrbanNetworkEdge,
} from './api';
import { candidateDistanceMeters } from './systemMapEdgeLayers';
import {
  findNearestEdge,
  findNearestStop,
  type NewStopDraft,
  type ReviewWorkflowState,
  useNewStopDraft,
  useSessionOverrides,
} from './systemMapReview';

export type LoadState = 'loading' | 'error' | 'ready';

export function normalizeStatus(status: string | null): StopStatusFilter {
  if (status === 'valid' || status === 'needs_review' || status === 'orphaned') return status;
  return 'other';
}

export function useSystemMapData(
  slug: string,
  token: string | null
): {
  routes: SystemMapRouteGeometry[];
  stops: SystemMapStopsResponse | null;
  loadState: LoadState;
} {
  const [routes, setRoutes] = useState<SystemMapRouteGeometry[]>([]);
  const [stops, setStops] = useState<SystemMapStopsResponse | null>(null);
  const [loadState, setLoadState] = useState<LoadState>('loading');

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    setLoadState('loading');
    Promise.all([clientPortalApi.getSystemMapRoutes(slug, token), clientPortalApi.getSystemMapStops(slug, token)])
      .then(([routesResp, stopsResp]) => {
        if (cancelled) return;
        setRoutes(routesResp.routes);
        setStops(stopsResp);
        setLoadState('ready');
      })
      .catch(() => {
        if (cancelled) return;
        setLoadState('error');
      });
    return (): void => {
      cancelled = true;
    };
  }, [slug, token]);

  return { routes, stops, loadState };
}

function useStopDetail(
  slug: string,
  token: string | null,
  stopId: string | null
): { detail: SystemMapStopDetail | null; loading: boolean; error: string | null } {
  const [detail, setDetail] = useState<SystemMapStopDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!stopId || !token) {
      setDetail(null);
      setError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    clientPortalApi
      .getSystemMapStopDetail(slug, token, stopId)
      .then(result => {
        if (cancelled) return;
        setDetail(result);
      })
      .catch(() => {
        if (cancelled) return;
        setError('No se pudo cargar el detalle de la parada.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return (): void => {
      cancelled = true;
    };
  }, [slug, token, stopId]);

  return { detail, loading, error };
}

/** Carga perezosa — solo pide los ~18.900 edges la primera vez que `enabled` pasa a true; después queda en caché para toda la sesión (nunca se re-pide al apagar/prender el toggle). */
export function useUrbanNetwork(
  slug: string,
  token: string | null,
  enabled: boolean
): { edges: UrbanNetworkEdge[] | null; loading: boolean } {
  const [edges, setEdges] = useState<UrbanNetworkEdge[] | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!enabled || edges !== null || !token) return;
    let cancelled = false;
    setLoading(true);
    clientPortalApi
      .getSystemMapUrbanNetwork(slug, token)
      .then(result => {
        if (cancelled) return;
        setEdges(result.edges);
      })
      .catch(() => {
        if (cancelled) return;
        setEdges([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return (): void => {
      cancelled = true;
    };
  }, [slug, token, enabled, edges]);

  return { edges, loading };
}

export interface MapSelectionState {
  selectedStopId: string | null;
  stopPoint: [number, number] | null;
  candidateEdge: UrbanNetworkEdge | null;
  candidateDistanceM: number | null;
  edgeOnly: UrbanNetworkEdge | null;
  selectStop: (stopId: string) => void;
  selectEdge: (edgeId: string) => void;
  closeStopPanel: () => void;
  closeEdgeOnlyPanel: () => void;
  clearCandidate: () => void;
  resetSelection: () => void;
}

/**
 * Toda la lógica de "qué está seleccionado en el mapa" en un solo lugar:
 * parada, candidato visual (solo tiene sentido mientras haya una parada
 * seleccionada — nunca sobrevive a un cambio de parada ni cambia la
 * asociación real) y edge-solo (inspección directa sin parada). Pide el
 * detail de la parada seleccionada internamente (mismo `useStopDetail` de
 * siempre) — el llamador nunca duplica ese fetch.
 */
export function useMapSelection(
  slug: string,
  token: string | null,
  urbanNetworkEdges: UrbanNetworkEdge[] | null
): MapSelectionState & { detail: SystemMapStopDetail | null; detailLoading: boolean; detailError: string | null } {
  const [selectedStopId, setSelectedStopId] = useState<string | null>(null);
  const [candidateEdgeId, setCandidateEdgeId] = useState<string | null>(null);
  const [edgeOnlyId, setEdgeOnlyId] = useState<string | null>(null);
  const { detail, loading: detailLoading, error: detailError } = useStopDetail(slug, token, selectedStopId);

  const stopPoint = useMemo<[number, number] | null>(
    () => (detail && detail.lon !== null && detail.lat !== null ? [detail.lon, detail.lat] : null),
    [detail]
  );
  const candidateEdge = useMemo(
    () => (candidateEdgeId ? urbanNetworkEdges?.find(e => e.id === candidateEdgeId) ?? null : null),
    [candidateEdgeId, urbanNetworkEdges]
  );
  const candidateDistanceM = useMemo(
    () => (candidateEdge && stopPoint ? candidateDistanceMeters(stopPoint, candidateEdge.geometry) : null),
    [candidateEdge, stopPoint]
  );
  const edgeOnly = useMemo(
    () => (edgeOnlyId ? urbanNetworkEdges?.find(e => e.id === edgeOnlyId) ?? null : null),
    [edgeOnlyId, urbanNetworkEdges]
  );

  return {
    selectedStopId,
    stopPoint,
    candidateEdge,
    candidateDistanceM,
    edgeOnly,
    detail,
    detailLoading,
    detailError,
    selectStop: stopId => {
      setSelectedStopId(stopId);
      setCandidateEdgeId(null);
      setEdgeOnlyId(null);
    },
    selectEdge: edgeId => {
      if (selectedStopId) setCandidateEdgeId(edgeId);
      else setEdgeOnlyId(edgeId);
    },
    closeStopPanel: () => {
      setSelectedStopId(null);
      setCandidateEdgeId(null);
    },
    closeEdgeOnlyPanel: () => setEdgeOnlyId(null),
    clearCandidate: () => setCandidateEdgeId(null),
    resetSelection: () => {
      setSelectedStopId(null);
      setCandidateEdgeId(null);
      setEdgeOnlyId(null);
    },
  };
}

/** `activeStatuses` + su toggle/reset en un solo hook — reduce el cuerpo de `SystemMapPage`. */
export function useStatusFilter(): {
  activeStatuses: Set<StopStatusFilter>;
  toggleStatus: (status: StopStatusFilter) => void;
  resetStatuses: () => void;
} {
  const [activeStatuses, setActiveStatuses] = useState<Set<StopStatusFilter>>(new Set(ALL_STATUSES));
  return {
    activeStatuses,
    toggleStatus: status => {
      setActiveStatuses(prev => {
        const next = new Set(prev);
        if (next.has(status)) next.delete(status);
        else next.add(status);
        return next;
      });
    },
    resetStatuses: () => setActiveStatuses(new Set(ALL_STATUSES)),
  };
}

/** Modo edición de UNA parada — nunca un toggle global; se resetea solo con la parada seleccionada. */
export function useEditMode(selectedStopId: string | null): {
  editing: boolean;
  toggle: () => void;
  cancel: () => void;
} {
  const [editing, setEditing] = useState(false);
  useEffect(() => {
    setEditing(false);
  }, [selectedStopId]);
  return { editing, toggle: () => setEditing(v => !v), cancel: () => setEditing(false) };
}

/** Cuando "Revisar pendientes" avanza (Anterior/Siguiente/Iniciar), selecciona esa parada — ref evita que el efecto dependa de la identidad de `selectStop` (cambia en cada render). */
export function useReviewAutoSelect(currentStopId: string | null, selectStop: (stopId: string) => void): void {
  const selectStopRef = useRef(selectStop);
  selectStopRef.current = selectStop;
  useEffect(() => {
    if (currentStopId) selectStopRef.current(currentStopId);
  }, [currentStopId]);
}

export interface NewStopComputed {
  proposedEdge: UrbanNetworkEdge | null;
  manualEdge: UrbanNetworkEdge | null;
  effectiveEdge: UrbanNetworkEdge | null;
  distanceM: number | null;
  nearestExisting: { id: string; name: string | null; distanceM: number } | null;
}

/** Deriva el edge propuesto/override manual, la distancia y el chequeo de duplicado del draft de "Nueva parada" — todo client-side, nada se guarda. */
export function useNewStopComputed(
  draft: NewStopDraft | null,
  urbanNetworkEdges: UrbanNetworkEdge[] | null,
  stops: SystemMapStopsResponse | null
): NewStopComputed {
  const proposedEdge = useMemo(
    () => (draft?.proposedEdgeId ? urbanNetworkEdges?.find(e => e.id === draft.proposedEdgeId) ?? null : null),
    [draft, urbanNetworkEdges]
  );
  const manualEdge = useMemo(
    () => (draft?.manualEdgeId ? urbanNetworkEdges?.find(e => e.id === draft.manualEdgeId) ?? null : null),
    [draft, urbanNetworkEdges]
  );
  const effectiveEdge = manualEdge ?? proposedEdge;
  const draftPoint = useMemo<[number, number] | null>(() => (draft ? [draft.lon, draft.lat] : null), [draft]);
  const distanceM = useMemo(
    () => (effectiveEdge && draftPoint ? candidateDistanceMeters(draftPoint, effectiveEdge.geometry) : null),
    [effectiveEdge, draftPoint]
  );
  const nearestExisting = useMemo(
    () => (draftPoint && stops ? findNearestStop(draftPoint, stops.stops) : null),
    [draftPoint, stops]
  );
  return { proposedEdge, manualEdge, effectiveEdge, distanceM, nearestExisting };
}

export interface DerivedMapProps {
  draftPoint: [number, number] | null;
  draftEdgeGeometry: GeoJSON.LineString | null;
  newStopArmed: boolean;
  confirmed: boolean;
  sessionOverride: import('./systemMapReview').StopOverride | undefined;
  stopsTotalLabel: string;
}

/** Junta varios valores derivados de una sola línea (ternarios/`||`/`??`) que si vivieran en `SystemMapPage` le sumarían complejidad ciclomática sin aportar nada — viven acá, en un hook aparte, con la misma semántica. */
export function useDerivedMapProps(
  newStopDraft: ReturnType<typeof useNewStopDraft>,
  draftComputed: NewStopComputed,
  selectedStopId: string | null,
  sessionOverrides: ReturnType<typeof useSessionOverrides>,
  stops: SystemMapStopsResponse | null
): DerivedMapProps {
  const draftPoint = useMemo<[number, number] | null>(
    () => (newStopDraft.draft ? [newStopDraft.draft.lon, newStopDraft.draft.lat] : null),
    [newStopDraft.draft]
  );
  const newStopArmed = newStopDraft.mode || newStopDraft.draft !== null;
  const confirmed = selectedStopId ? sessionOverrides.overrides.has(selectedStopId) : false;
  const sessionOverride = selectedStopId ? sessionOverrides.getOverride(selectedStopId) : undefined;
  const stopsTotalLabel = stops ? String(stops.counts.total) : '…';
  return {
    draftPoint,
    draftEdgeGeometry: draftComputed.effectiveEdge?.geometry ?? null,
    newStopArmed,
    confirmed,
    sessionOverride,
    stopsTotalLabel,
  };
}

export interface SystemMapPageActions {
  handleSelectEdge: (edgeId: string) => void;
  handleToggleEdit: () => void;
  handleCancelEdit: () => void;
  handleConfirmInSession: () => void;
  handleToggleNewStop: () => void;
  handlePlaceDraft: (lon: number, lat: number) => void;
  resetFilters: () => void;
}

/** Todos los handlers de `SystemMapPage` en un solo hook — nada de esto persiste nada, ver cada método para el detalle de qué toca (solo estado de React). */
export function useSystemMapPageActions(args: {
  selection: MapSelectionState & { detail: SystemMapStopDetail | null };
  editMode: ReturnType<typeof useEditMode>;
  sessionOverrides: ReturnType<typeof useSessionOverrides>;
  newStopDraft: ReturnType<typeof useNewStopDraft>;
  urbanNetworkEdges: UrbanNetworkEdge[] | null;
  setUrbanNetworkEnabled: (v: boolean) => void;
  setSelectedLineId: (v: string | null) => void;
  resetStatuses: () => void;
}): SystemMapPageActions {
  const { selection, editMode, sessionOverrides, newStopDraft, urbanNetworkEdges, setUrbanNetworkEnabled, setSelectedLineId, resetStatuses } =
    args;

  return {
    handleSelectEdge: edgeId => {
      if (newStopDraft.draft) {
        newStopDraft.updateDraft({ manualEdgeId: edgeId });
        return;
      }
      selection.selectEdge(edgeId);
    },
    handleToggleEdit: () => {
      if (!editMode.editing) setUrbanNetworkEnabled(true);
      editMode.toggle();
    },
    handleCancelEdit: () => {
      editMode.cancel();
      selection.clearCandidate();
    },
    handleConfirmInSession: () => {
      if (selection.selectedStopId) {
        sessionOverrides.confirmInSession(selection.selectedStopId, selection.detail?.status ?? null);
      }
    },
    handleToggleNewStop: () => {
      if (newStopDraft.mode || newStopDraft.draft) {
        newStopDraft.disarm();
        newStopDraft.clearDraft();
        return;
      }
      setUrbanNetworkEnabled(true);
      selection.resetSelection();
      newStopDraft.arm();
    },
    handlePlaceDraft: (lon, lat) => {
      const nearest = urbanNetworkEdges ? findNearestEdge([lon, lat], urbanNetworkEdges) : null;
      newStopDraft.placeDraft(lon, lat, nearest?.edge.id ?? null);
    },
    resetFilters: () => {
      setSelectedLineId(null);
      resetStatuses();
      selection.resetSelection();
    },
  };
}

/** Arma el bag de props de `SystemMapCanvasArea` (vía `SystemMapMapWrap`) — solo para sacar ese objeto literal largo del cuerpo de `SystemMapPage`, sin cambiar nada de la semántica. */
export function useCanvasAreaProps(args: {
  routes: SystemMapRouteGeometry[];
  visibleStops: SystemMapStopsResponse['stops'];
  selectedLineId: string | null;
  setSelectedLineId: (id: string | null) => void;
  activeStatuses: Set<StopStatusFilter>;
  toggleStatus: (status: StopStatusFilter) => void;
  actions: SystemMapPageActions;
  urbanNetworkEnabled: boolean;
  urbanNetworkEdges: UrbanNetworkEdge[] | null;
  urbanNetworkLoading: boolean;
  setUrbanNetworkEnabled: (v: boolean) => void;
  selection: MapSelectionState & { detail: SystemMapStopDetail | null };
  review: ReviewWorkflowState;
  newStopDraft: ReturnType<typeof useNewStopDraft>;
  derived: DerivedMapProps;
  sessionOverrideIds: string[];
}) {
  const {
    routes,
    visibleStops,
    selectedLineId,
    setSelectedLineId,
    activeStatuses,
    toggleStatus,
    actions,
    urbanNetworkEnabled,
    urbanNetworkEdges,
    urbanNetworkLoading,
    setUrbanNetworkEnabled,
    selection,
    review,
    newStopDraft,
    derived,
    sessionOverrideIds,
  } = args;
  return {
    routes,
    visibleStops,
    selectedLineId,
    onSelectLineId: setSelectedLineId,
    activeStatuses,
    onToggleStatus: toggleStatus,
    onReset: actions.resetFilters,
    urbanNetworkEnabled,
    urbanNetworkEdges,
    urbanNetworkLoading,
    onToggleUrbanNetwork: () => setUrbanNetworkEnabled(!urbanNetworkEnabled),
    selection,
    onSelectEdge: actions.handleSelectEdge,
    review,
    draftMode: newStopDraft.mode,
    draftPoint: derived.draftPoint,
    draftEdgeGeometry: derived.draftEdgeGeometry,
    sessionOverrideIds,
    onPlaceDraft: actions.handlePlaceDraft,
    onStartReview: review.start,
    newStopArmed: derived.newStopArmed,
    onToggleNewStop: actions.handleToggleNewStop,
  };
}
